import { useState } from 'react';
import { Box, Button, LinearProgress, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface ConsentStepLandingProps {
  /** Zero-based index of the consent step that's about to run. */
  stepIndex: number;
  /** Total number of consent steps in the chain. */
  totalSteps: number;
  /** Human-readable name of the app the admin is about to consent to. */
  appName: string;
  /**
   * App logo as a data URI (`data:{mediaType};base64,{data}`), or `null`
   * when the app registration has no logo.
   */
  appIcon?: string | null;
  /**
   * Triggered when the admin clicks the consent button. Implementation is
   * expected to navigate the browser to Azure AD.
   */
  onConsent: () => void;
}

/**
 * Landing screen shown before each Azure AD admin-consent redirect. Gives
 * the admin a chance to see which app they're about to grant permissions
 * to and shows the progress through the chain.
 */
export const ConsentStepLanding = ({
  stepIndex,
  totalSteps,
  appName,
  appIcon,
  onConsent,
}: ConsentStepLandingProps) => {
  const { t } = useTranslation();
  const [redirecting, setRedirecting] = useState(false);
  const stepNumber = stepIndex + 1;
  const progressValue = (stepNumber / totalSteps) * 100;

  const handleClick = () => {
    setRedirecting(true);
    onConsent();
  };

  return (
    <Stack spacing={2.5} alignItems="center">
      <Typography variant="overline" color="text.secondary">
        {t('signup:consentStep:registerCallbackPage:eyebrow', {
          current: stepNumber,
          total: totalSteps,
        })}
      </Typography>
      {appIcon && (
        <Box
          component="img"
          src={appIcon}
          alt={appName}
          sx={{ width: 64, height: 64, borderRadius: 1, objectFit: 'contain' }}
        />
      )}
      <Typography variant="h5">
        {t('signup:consentStep:registerCallbackPage:title', { appName })}
      </Typography>
      <Box sx={{ width: '100%' }}>
        <LinearProgress
          variant="determinate"
          value={progressValue}
          sx={{ height: 8, borderRadius: 4 }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 0.5, textAlign: 'right' }}
        >
          {Math.round(progressValue)} %
        </Typography>
      </Box>
      <Typography color="text.primary">
        {t('signup:consentStep:registerCallbackPage:description', { appName })}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('signup:consentStep:registerCallbackPage:hint')}
      </Typography>
      <Button
        variant="contained"
        color="primary"
        size="large"
        onClick={handleClick}
        disabled={redirecting}
        sx={{ mt: 1, minWidth: 280 }}
      >
        {redirecting
          ? t('signup:consentStep:registerCallbackPage:button_loading')
          : t('signup:consentStep:registerCallbackPage:button_grant', { appName })}
      </Button>
    </Stack>
  );
};
